import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';

import { TwcInputFileAddMethod, TwcInputFileComponent, TwcInputFileWarning } from '@topcon/twc-angular';

import { MagnetService } from '@mirage-projects/cw-commons';

export type InputFileDialogMode = 'internal' | 'external' | 'disabled';

@Component({
    selector: 'cw-input-file',
    templateUrl: './input-file.component.html',
    styleUrls: ['./input-file.component.scss'],
    standalone: false,
})
export class InputFileComponent implements AfterViewInit {
    /** If is true, hide the files list */
    @Input() hideListFiles = false;
    /** Whether to show warnings using notifications or process them manually */
    @Input() showWarnings = true;
    /**
     * What to do while adding files. Update or accumulate.
     *  "accumulate" - every time when you add new files they will be accumulated.
     *  "replace" - every time when you add new files previous will be removed.
     */
    @Input() addMethod: TwcInputFileAddMethod = 'replace';
    /** How many files allowed to attach. */
    @Input() maxFiles = 1;
    /**
     * 	Acceptable file types, which takes a comma-separated list of allowed file extensions or MIME types.
     * Some examples:
     *** accept="image/png" or accept=".png" -> Accepts PNG files.
     *** accept="image/png, image/jpeg" or accept=".png, .jpg, .jpeg" -> Accept PNG or JPEG files.
     *** accept="image/*" ->  Accept any file with an image/* MIME type. (Many mobile devices also let the user take a picture with the camera when this is used.)
     *** accept=".doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" ->
     *    accept anything that smells like an MS Word document
     */
    @Input() accept = '*';
    /* if availableSize is other than null, the total size to be uploaded and the available storage after the upload will be shown */
    @Input() availableSize: number | null = null;
    /** Loaded files at init */
    @Input() files: File[] = [];

    /** If should open the file/folder select dialog after component load */
    @Input() isAutoOpenEnabled = false;

    /** If should use an external dialog handler */
    @Input() dialogMode: InputFileDialogMode = 'internal';

    /** If should show loader */
    @Input() isLoading = false;

    /** Emits after adding files. */
    @Output() added = new EventEmitter<File[]>();
    /** Emits message to open an external dialog */
    @Output() openExternalDialog = new EventEmitter<void>();
    /**  Emits while adding files if some files not pass validation. */
    @Output() warning = new EventEmitter<TwcInputFileWarning[]>();

    @ViewChild(TwcInputFileComponent) twcInput!: TwcInputFileComponent;

    constructor(private magnetService: MagnetService) {}

    ngAfterViewInit(): void {
        if (this.isAutoOpenEnabled) {
            this.onDialogOpen();
        }
    }

    onDialogOpen(): void {
        if (this.isLoading) {
            return;
        }

        switch (this.dialogMode) {
            case 'internal':
                this.twcInput.browse();
                return;
            case 'external':
                this.openExternalDialog.emit();
                return;
            default:
                return;
        }
    }

    /** @ignore */
    emitAdded(files: File[]): void {
        this.added.emit(files);
    }

    /** @ignore */
    emitWarning(warnings: TwcInputFileWarning[]): void {
        this.warning.emit(warnings);
    }

    /** @ignore */
    getCurrentAvailableSize(): number {
        if (!this.availableSize) {
            return 0;
        }
        return this.availableSize - this.getTotalFilesSize();
    }

    /** @ignore */
    getTotalFilesSize(): number {
        if (!this.files) {
            return 0;
        }
        let totalSize = 0;
        this.files.forEach((f) => (totalSize += f.size));
        return totalSize;
    }

    /* istanbul ignore next */
    get hasToShowCurrentAvailableSize(): boolean {
        return !!this.availableSize && !this.magnetService.isSite();
    }
}
